import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, Trash2 } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { BTN_PRIMARY, OrderStatusPill } from "@/components/orders/ui";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { aed, STATUS_LABEL, type Agent, type OrderRow, type OrderStatus } from "@/lib/orders";
import { formatDate } from "@/lib/utils";

const STATUSES: OrderStatus[] = ["draft", "pending_approval", "sent", "signed", "cancelled"];

export default function OrdersPage() {
  const { t, locale } = useLocale();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<OrderRow[] | null>(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [agent, setAgent] = useState("");
  const [agents, setAgents] = useState<Agent[]>([]);

  useEffect(() => {
    api.orderAgents().then(setAgents).catch(() => setAgents([]));
  }, []);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(
    () =>
      api
        .orders({ status: status || undefined, q: q || undefined, assignee: agent || undefined })
        .then(setRows)
        .catch(() => setRows([])),
    [status, q, agent],
  );

  useEffect(() => {
    const handle = setTimeout(() => void load(), 250);
    return () => clearTimeout(handle);
  }, [load]);

  const duplicate = async (r: OrderRow) => {
    setBusyId(r.id);
    setNotice(null);
    try {
      const copy = await api.duplicateOrder(r.id);
      navigate(`/orders/${copy.id}`);
    } catch {
      setNotice(`Could not duplicate ${r.number}.`);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r: OrderRow) => {
    if (!window.confirm(`Delete order ${r.number} (${r.customerName})? This cannot be undone.`)) return;
    setBusyId(r.id);
    setNotice(null);
    try {
      await api.deleteOrder(r.id);
      setNotice(`Order ${r.number} deleted.`);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      setNotice(msg.includes("project") ? `${r.number} already became a project — cancel it instead.` : `Could not delete ${r.number}.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="brand-kicker">{t.groupSales}</div>
          <h1 className="font-serif text-2xl text-paper sm:text-3xl">{t.orders}</h1>
        </div>
        <Link to="/orders/new" className={BTN_PRIMARY}>
          + {t.newOrder}
        </Link>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="field w-auto">
          <option value="" className="bg-night">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s} className="bg-night">{STATUS_LABEL[s]}</option>
          ))}
        </select>
        <select value={agent} onChange={(e) => setAgent(e.target.value)} className="field w-auto">
          <option value="" className="bg-night">All agents</option>
          {user && <option value={user.id} className="bg-night">My orders</option>}
          {agents.filter((a) => a.id !== user?.id).map((a) => (
            <option key={a.id} value={a.id} className="bg-night">{a.name}</option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search number, customer, mobile…"
          className="field max-w-sm flex-1"
        />
      </div>

      {notice && <p className="mt-3 text-xs text-goldbright">{notice}</p>}

      <div className="glass-panel mt-4 overflow-x-auto">
        {rows === null ? (
          <div className="p-6 text-sm text-ice/60">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-ice/60">No orders yet. Create the first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[0.6rem] uppercase tracking-[0.18em] text-ice/50">
                <th className="px-4 py-3">Number</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3 text-right">Total AED</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3">Signed</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/orders/${r.id}`)}
                  className="cursor-pointer border-t border-white/8 transition hover:bg-white/6"
                >
                  <td className="px-4 py-3 font-semibold text-paper">
                    {r.number}
                    {r.version > 1 && <span className="ml-1 text-[0.6rem] text-ice/50">v{r.version}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-paper/90">{r.customerName}</div>
                    {r.company && <div className="text-[0.7rem] text-ice/55">{r.company}</div>}
                  </td>
                  <td className="num px-4 py-3 text-ice/80" dir="ltr">+{r.mobile}</td>
                  <td className="px-4 py-3 text-ice/80">{r.assigneeName ?? "—"}</td>
                  <td className="num px-4 py-3 text-right font-semibold text-goldbright">{aed(r.total)}</td>
                  <td className="px-4 py-3"><OrderStatusPill status={r.status} /></td>
                  <td className="num px-4 py-3 text-ice/70">{formatDate(r.createdAt, locale)}</td>
                  <td className="num px-4 py-3 text-ice/70">{formatDate(r.sentAt, locale)}</td>
                  <td className="num px-4 py-3 text-ice/70">{formatDate(r.signedAt, locale)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        title="Duplicate as a new draft"
                        disabled={busyId !== null}
                        onClick={() => void duplicate(r)}
                        className="rounded-full border border-white/15 p-1.5 text-ice/70 transition hover:border-white/40 hover:text-paper disabled:opacity-50"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          title={r.projectId ? "Already a project — cancel instead" : "Delete order"}
                          disabled={busyId !== null || Boolean(r.projectId)}
                          onClick={() => void remove(r)}
                          className="rounded-full border border-white/15 p-1.5 text-ice/70 transition hover:border-rose/60 hover:text-pink disabled:opacity-40"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}
